/**
 * AI:
 * Status: "in progress"
 * Type: Page
 * SubType: CreatePage
 * Reason: [CO-APP R3/A] The Kundenanlage of the Co-App (shell-consultant, route `cr/create`): the same
 *   <ci-customer-create-wizard> run as shell-alpha, but without the tenant-wide „created in the last 7 days"
 *   list, with the new customer assigned to the logged-on agent and opened in the SAME tab.
 */
import {Component, OnDestroy} from '@angular/core';
import {ActivatedRoute, Router} from "@angular/router";
import {HttpErrorResponse} from "@angular/common/http";
import {CrCustomerCreatePage} from "../../object/cr-customer-create/cr-customer-create.page";
import {MvsCoreService} from "../../../../../core/shared/service/mvs-core.service";
import {CustomerService} from "../../../service/api/customer.service";
import {MvsObjectNavigationService} from "../../../../../core/shared/navigation/mvs-object-navigation.service";
import {MvsMessageService} from "../../../../../core/shared/service/message/mvs-message.service";
import {
    CiCustomerCreateWizardResult
} from "../../../../ci/component/protected-components/ci-customer-create-wizard/ci-customer-create-wizard.component";

/**
 * [CO-APP R3/A] The Kundenanlage of the Co-App.
 *
 * <h3>Why this EXTENDS CrCustomerCreatePage</h3>
 * The step set of a standalone Kundenanlage - which of the wizard's steps are hard-skipped and why
 * (`buildCustomerCreateWizardSpec`) - stays in ONE place. This page only changes what is shown around
 * the wizard and what happens when a run ends.
 *
 * <h3>What is different from shell-alpha</h3>
 * <ul>
 *   <li>NO „Kunden angelegt in den letzten 7 Tagen" widget: that list is tenant-wide and would show an
 *   agent customers of other agents (data protection). {@link refreshWidgetCreatedCustomers} is a no-op
 *   and the template renders the wizard in full width.</li>
 *   <li>A completed run assigns the new customer to the logged-on agent BEFORE the customer is opened -
 *   without that link the agent would not find the customer again.</li>
 *   <li>The customer opens in the SAME tab, relative to the shell prefix route, so `/mobile` survives.</li>
 * </ul>
 *
 * <h3>A failed assignment is answered ON THIS PAGE, before navigating</h3>
 * „Erneut versuchen" has to stay reachable, and a toast action would not survive the navigation to the
 * customer page. So a failed assignment keeps the user here with a notice offering „Erneut versuchen"
 * and „Kunde trotzdem öffnen". The one exception is a FINAL business refusal of the assignment
 * ({@link isFinalAssignmentRefusal}) - e.g. no agent stored for the logged-on user: a retry cannot change
 * that, so its message is shown and the customer is opened right away.
 *
 * <h3>Where this page is DECLARED</h3>
 * In CiModule, next to its base class and for the same reason (BOUND-002): the template hosts
 * <ci-customer-create-wizard>, which only CiModule declares.
 */
@Component({
    selector: 'mvs-cr-customer-create-consultant-page',
    templateUrl: './cr-customer-create-consultant.page.html',
    standalone: false
})
export class CrCustomerCreateConsultantPage extends CrCustomerCreatePage implements OnDestroy {

    /**
     * A finished run is being answered - from the wizard's finish until the navigation settled or the
     * next run started, INCLUDING the time the failed-assignment notice waits for the user.
     *
     * Guards against a second handling of a finish while the first one is still open.
     */
    protected finishInFlight: boolean = false;

    /**
     * A request (assignment, access check) or the navigation is running. Drives the busy hint and
     * disables the notice's buttons, so a double click cannot fire a second call or a second navigation.
     */
    requestRunning: boolean = false;

    /**
     * The customer whose assignment failed and who is waiting for the user's decision („Erneut versuchen"
     * or „Kunde trotzdem öffnen"). Null while no such notice is shown.
     */
    pendingAssignmentCustomerId: number = null;

    /** Set on destroy: a call that returns after the user left this page must not pull them back. */
    protected destroyed: boolean = false;

    constructor(
        route: ActivatedRoute,
        coreService: MvsCoreService,
        customerService: CustomerService,
        objectNavigationService: MvsObjectNavigationService,
        router: Router,
        protected messageService: MvsMessageService) {
        super(route, coreService, customerService, objectNavigationService, router);
    }

    /**
     * No „created in the last 7 days" widget in the Co-App - the list is tenant-wide, see the class TSDoc.
     */
    override refreshWidgetCreatedCustomers(): void {
        // intentionally empty
    }

    /**
     * The run ended - emitted exactly once per run.
     * <ul>
     *   <li>`completed` - assign the new customer to the logged-on agent, then open it. See the class
     *   TSDoc for what a failed assignment does. Never a rollback, never an automatic retry.</li>
     *   <li>`assignInstead` - the found person already is a customer. Nothing is written; the customer is
     *   opened if the agent may read it, otherwise the agent is told so and a new run starts.</li>
     *   <li>`cancelled` - no navigation, a new run starts (same as the base page).</li>
     * </ul>
     */
    override handleCustomerCreateFinished(result: CiCustomerCreateWizardResult): void {

        if (this.finishInFlight) {
            return;
        }

        switch (result?.outcome) {
            case 'completed':
                this.finishCompleted(result.context?.customerId);
                break;
            case 'assignInstead':
                this.finishAssignInstead(result.assignCustomerId);
                break;
            default:
                this.startNextRun();
        }
    }

    /**
     * `completed`: assign to the logged-on agent, then open the customer.
     * @param customerId the customer the run created
     */
    protected finishCompleted(customerId: number): void {

        if (!customerId) {
            this.startNextRun();
            return;
        }

        this.finishInFlight = true;
        this.assignAndOpenCustomer(customerId);
    }

    /**
     * „Erneut versuchen" - the endpoint is idempotent, so a repeated call cannot create a second link.
     */
    handleRetryAssignment(): void {

        if (this.requestRunning || !this.pendingAssignmentCustomerId) {
            return;
        }

        this.assignAndOpenCustomer(this.pendingAssignmentCustomerId);
    }

    /**
     * „Kunde trotzdem öffnen" - navigate without any further assignment call.
     */
    handleOpenCustomerWithoutAssignment(): void {

        if (this.requestRunning || !this.pendingAssignmentCustomerId) {
            return;
        }

        const customerId = this.pendingAssignmentCustomerId;
        this.pendingAssignmentCustomerId = null;
        this.openCustomer(customerId);
    }

    /**
     * One assignment call and its answer: success opens the customer, a final refusal shows its message
     * and opens the customer, every other failure shows the notice with the retry option.
     * @param customerId
     */
    protected assignAndOpenCustomer(customerId: number): void {

        this.requestRunning = true;

        this.customerService.assignLoggedOnAgent(customerId).subscribe({
            next: () => {
                this.pendingAssignmentCustomerId = null;
                this.openCustomer(customerId);
            },
            error: (error: HttpErrorResponse) => {

                if (this.isFinalAssignmentRefusal(error)) {
                    this.pendingAssignmentCustomerId = null;

                    const message = this.getFinalRefusalMessage(error);
                    if (message) {
                        this.messageService.showErrorMessage('Kundenzuordnung', message);
                    }

                    this.openCustomer(customerId);
                    return;
                }

                this.requestRunning = false;
                this.pendingAssignmentCustomerId = customerId;
            }
        });
    }

    /**
     * `assignInstead`: read the existing customer first - only a customer the agent may read is opened.
     * @param customerId the customer the found person already is
     */
    protected finishAssignInstead(customerId: number): void {

        if (!customerId) {
            this.startNextRun();
            return;
        }

        this.finishInFlight = true;
        this.requestRunning = true;

        this.customerService.get(customerId).subscribe({
            next: () => this.openCustomer(customerId),
            error: (error: HttpErrorResponse) => {
                const message = error?.status === 403
                    ? 'Die Person ist bereits Kunde, Sie haben auf diesen Kunden jedoch keinen Zugriff.'
                    : 'Die Person ist bereits Kunde, der Kunde konnte jedoch nicht geöffnet werden.';
                this.messageService.showErrorMessage('Kundenanlage', message);
                this.startNextRun();
            }
        });
    }

    /**
     * Is this a FINAL business refusal of the assignment - one a retry cannot change?
     *
     * Every 409 of the endpoint: no agent stored for the logged-on user, customer already assigned to
     * another agent, customer not recently created by the logged-on user. The ONE place that knows how
     * the server signals these: 409 with a `CoreApiResponseDto` whose `data` carries the German message
     * (decision #27). Deliberately NOT coupled to the message text.
     * @param error
     */
    protected isFinalAssignmentRefusal(error: HttpErrorResponse): boolean {
        return error?.status === 409;
    }

    /**
     * The toast text for a final refusal ({@link isFinalAssignmentRefusal}), or null when the global HttpErrorInterceptor already shows
     * the server's message - a second toast with the same text would only be noise.
     *
     * Mirrors the interceptor's own rule (features/core/shared/error/http.error.intercepter.ts): a body
     * with `codes` is passed on silently, a body with `data` is toasted as it is. Anything else - a plain
     * string body, no body - gets its text shown here, or the generic hint to contact the back office.
     * @param error
     */
    protected getFinalRefusalMessage(error: HttpErrorResponse): string | null {

        const body = error?.error;

        if (body && !body.codes && body.data) {
            return null;
        }

        const serverMessage = typeof body === 'string' ? body : body?.data;

        if (typeof serverMessage === 'string' && serverMessage.trim()) {
            return serverMessage;
        }

        return 'Der Kunde wurde angelegt, die Zuordnung zu Ihnen ist fehlgeschlagen. '
            + 'Bitte wenden Sie sich an den Innendienst.';
    }

    /**
     * Open the customer in the SAME tab.
     *
     * Relative to the PARENT route: shell-consultant mounts every sub route twice (under '' and under
     * 'mobile'), and this page is routed as one full path directly below that prefix route. Commands
     * relative to it keep `/mobile`; an absolute '/cr/customers/..' would drop it. Without a parent the
     * router resolves against the root, i.e. the absolute URL.
     * @param customerId
     */
    protected openCustomer(customerId: number): void {

        if (this.destroyed) {
            return;
        }

        this.requestRunning = true;

        this.router.navigate(['cr', 'customers', customerId], {relativeTo: this.route?.parent ?? null})
            .catch(() => false)
            .then(() => this.startNextRun());
    }

    /**
     * A FRESH spec starts the next run - same as the base page, see its `handleCustomerCreateFinished`.
     * Also after a successful navigation: should the navigation have been refused, the page still offers
     * an empty Kundenanlage instead of a finished stepper.
     */
    protected startNextRun(): void {
        this.finishInFlight = false;
        this.requestRunning = false;
        this.pendingAssignmentCustomerId = null;
        this.customerCreateWizardSpec = this.buildCustomerCreateWizardSpec();
    }

    override ngOnDestroy(): void {
        this.destroyed = true;
        super.ngOnDestroy();
    }
}
